import { useState } from 'react';

import PropTypes from 'prop-types';

import Button from '#globalComponents/Button/Button';
import ProgressBar from '#globalComponents/ProgressBar/ProgressBar';

const ROWS = [
	{ key: 'companies', label: 'Firmen', created: 'companiesCreated', failed: 'companiesFailed' },
	{
		key: 'contacts',
		label: 'Kontakte',
		created: 'contactsCreated',
		skipped: 'contactsSkipped',
		failed: 'contactsFailed',
	},
	{ key: 'deals', label: 'Deals', created: 'dealsCreated', failed: 'dealsFailed' },
	{
		key: 'activities',
		label: 'Aktivitäten',
		created: 'activitiesCreated',
		skipped: 'activitiesSkipped',
		failed: 'activitiesFailed',
	},
	{ key: 'notes', label: 'Notizen', created: 'notesCreated', skipped: 'notesSkipped', failed: 'notesFailed' },
];

export default function PipedriveResultStep({ result, isLoading, totalRows, onClose }) {
	const [showErrors, setShowErrors] = useState(false);

	if (isLoading && (!result || result.status === 'processing')) {
		const processed = result?.processedRows ?? 0;
		const pct = totalRows > 0 ? Math.round((processed / totalRows) * 100) : 0;
		return (
			<div className="flex flex-col items-center justify-center py-16">
				<div className="w-full max-w-md mb-4">
					<div className="flex justify-between text-sm text-text mb-1">
						<span>
							{processed.toLocaleString('de-DE')} / {totalRows.toLocaleString('de-DE')} Zeilen
						</span>
						<span>{pct}%</span>
					</div>
					<ProgressBar value={processed} max={totalRows} className="h-3" />
				</div>
				<p className="text-sm text-bgBase-800">Migration läuft... das kann bei großen Exporten dauern.</p>
			</div>
		);
	}

	const summary = result?.summary ?? {};
	const errors = result?.errors ?? [];
	const failed = result?.status === 'failed';

	return (
		<div>
			{failed && (
				<div className="p-4 bg-warning-200 border border-warning-500/40 rounded-card mb-4">
					<p className="text-sm text-warning-700 font-medium">Die Migration ist fehlgeschlagen.</p>
				</div>
			)}

			<div className="border border-bgBase-500 rounded-card overflow-hidden mb-4">
				<table className="w-full text-sm">
					<thead className="bg-bgBase-300">
						<tr>
							<th className="text-left px-4 py-2 font-medium text-text">Entität</th>
							<th className="text-right px-4 py-2 font-medium text-text">Angelegt</th>
							<th className="text-right px-4 py-2 font-medium text-text">Übersprungen</th>
							<th className="text-right px-4 py-2 font-medium text-text">Fehler</th>
						</tr>
					</thead>
					<tbody>
						{ROWS.map((r) => (
							<tr key={r.key} className="border-t border-bgBase-400">
								<td className="px-4 py-2 text-text">{r.label}</td>
								<td className="px-4 py-2 text-right text-success-700">{summary[r.created] ?? 0}</td>
								<td className="px-4 py-2 text-right text-bgBase-800">
									{r.skipped ? (summary[r.skipped] ?? 0) : '—'}
								</td>
								<td className="px-4 py-2 text-right text-warning-700">{summary[r.failed] ?? 0}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{summary.contactsSkipped > 0 && (
				<p className="text-xs text-bgBase-800 mb-4">
					{summary.contactsSkipped} Kontakt(e) ohne Organisation in Pipedrive wurden übersprungen (im CRM muss
					jeder Kontakt einer Firma zugeordnet sein).
				</p>
			)}

			{errors.length > 0 && (
				<div className="mt-4">
					<button
						onClick={() => setShowErrors(!showErrors)}
						className="text-sm text-warning-700 hover:text-warning-700 font-medium"
					>
						{showErrors ? 'Fehler ausblenden' : `${errors.length} Fehler anzeigen`}
					</button>
					{showErrors && (
						<div className="mt-2 border border-warning-500/40 rounded-card max-h-[200px] overflow-y-auto">
							<table className="w-full text-sm">
								<tbody>
									{errors.slice(0, 200).map((err, i) => (
										<tr key={i} className="border-t border-warning-500/40">
											<td className="px-3 py-1.5 text-warning-700">{err.entity ?? '—'}</td>
											<td className="px-3 py-1.5 text-warning-700">Zeile {err.row ?? '?'}</td>
											<td className="px-3 py-1.5 text-warning-700">{err.message}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			)}

			<div className="flex justify-end mt-6">
				<Button onClick={onClose}>Fertig</Button>
			</div>
		</div>
	);
}

PipedriveResultStep.propTypes = {
	result: PropTypes.object,
	isLoading: PropTypes.bool,
	totalRows: PropTypes.number,
	onClose: PropTypes.func.isRequired,
};
